import type { RegisteredRouter, RouteIds } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { useFilters } from '@/utils/use-search-params'

import { FiltersStateContext, type FiltersStateContextValue } from './filters-state-context'

interface FiltersStateProviderProps<TId extends RouteIds<RegisteredRouter['routeTree']>> {
  routeId: TId
  children: ReactNode
}

/**
 * Binds the filter components to the search params of `routeId`. The cast to the
 * loose {@link FiltersStateContextValue} is contained here so the consuming
 * filter components stay route-agnostic and fully typed.
 */
export function FiltersStateProvider<TId extends RouteIds<RegisteredRouter['routeTree']>>({
  routeId,
  children,
}: FiltersStateProviderProps<TId>) {
  const { filters, setFilters } = useFilters(routeId)

  // `routeId` est générique : le search vu ici est l'union de toutes les routes
  // enregistrées, que TypeScript ne peut pas rapprocher du contrat du contexte.
  // Chaque membre est élargi séparément, à sa propre place dans le contrat.
  const value: FiltersStateContextValue = {
    filters: filters as FiltersStateContextValue['filters'],
    setFilters: setFilters as FiltersStateContextValue['setFilters'],
  }

  return <FiltersStateContext.Provider value={value}>{children}</FiltersStateContext.Provider>
}
